import React from 'react';
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';

import TextFieldBase from '@/components/mui/Form/TextField/TextFieldBase';
import helperTextError from '@/components/mui/Form/TextField/helperTextError';

type TextFieldBaseProps = React.ComponentProps<typeof TextFieldBase>;

interface TextFieldRhfProps<T extends FieldValues>
  extends Omit<TextFieldBaseProps, 'name' | 'value' | 'defaultValue' | 'error' | 'helperText'> {
  name: FieldPath<T>;
  control: Control<T>;
  // Standing hint under the field. A validation message takes its place while
  // one is showing, so the two can never hide each other.
  description?: React.ReactNode;
}

// Whitespace policy belongs to the field's zod schema (`z.string().trim()`): the
// resolver's output is what reaches onSubmit, so the value is normalised without
// ever rewriting what the user is typing.
const TextFieldRhf = <T extends FieldValues>({ name, control, description, ...rest }: TextFieldRhfProps<T>) => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState }) => (
      <TextFieldBase
        {...field}
        // MUI switches an input to uncontrolled as soon as `value` is undefined,
        // so a field absent from defaultValues must still resolve to ''.
        value={field.value ?? ''}
        variant="outlined"
        error={Boolean(fieldState.error)}
        // Rendered only when there is something to say, which is MUI's own
        // behaviour. A message replaces the standing description rather than
        // joining it — MUI colours the whole helper slot red, so the two cannot
        // share it.
        helperText={helperTextError(fieldState.error?.message) ?? description}
        {...rest}
      />
    )}
  />
);

export default TextFieldRhf;
